import axios from "axios";
import { useEffect, useState } from "react";

// MUI
import { CalendarMonth, CardGiftcard, Check, ContentCopy, EmojiEvents, People, Person, Share, WhatsApp } from '@mui/icons-material';
import { Box, Button, Card, CardContent, Chip, CircularProgress, Container, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Divider, Grid, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material';

export default function RewardsStudent() {
    const apiUrl = process.env.REACT_APP_API;
    const [copied, setCopied] = useState(false);
    const [shareCopied, setShareCopied] = useState(false);
    const [loading, setLoading] = useState(true);
    const [studentData, setStudentData] = useState({
        referralCode: "",
        name: "",
        totalReferrals: 0,
        totalRewards: 0
    });

    const get_details = async () => {
        try {
            const response = await axios.post(`${apiUrl}/student/details`);
            if (response.data.status === true) {
                const d = response.data.data;
                setStudentData({
                    referralCode: d.studentid || "",
                    name: d.name || "",
                    totalReferrals: d.total_referrals || 0,
                    totalRewards: d.total_rewards || 0
                });
            }
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    const [rewardHistory, setRewardHistory] = useState([]);
    const [rewardLoading, setRewardLoading] = useState(true);
    const [rewardSummary, setRewardSummary] = useState({ total_referral: 0, total_credit: 0, total_debit: 0, balance: 0 });
    const [rewardPagination, setRewardPagination] = useState({ total_pages: 1, current_page: 1 });
    const [rewardPage, setRewardPage] = useState(1);

    const fetchRewards = async (page = 1) => {
        setRewardLoading(true);
        try {
            const res = await axios.get(`${apiUrl}/student/reward/getall?page=${page}`);
            if (res.data.status === true || res.data.status === 'success') {
                setRewardHistory(res.data.data || []);
                if (res.data.summary) setRewardSummary(res.data.summary);
                if (res.data.pagination) setRewardPagination(res.data.pagination);
            }
        } catch (e) {
            // silently fail
        } finally {
            setRewardLoading(false);
        }
    };

    useEffect(() => {
        window.scroll(0, 0);
        get_details();
        fetchRewards(1);
    }, []);

    const handlePageChange = (newPage) => {
        setRewardPage(newPage);
        fetchRewards(newPage);
    };

    // ── Withdraw ──────────────────────────────────────────────────
    const [withdrawAmount, setWithdrawAmount] = useState('');
    const [withdrawing, setWithdrawing] = useState(false);
    const [confirmWithdraw, setConfirmWithdraw] = useState(false);

    const handleWithdraw = async () => {
        setConfirmWithdraw(false);
        setWithdrawing(true);
        try {
            const formData = new FormData();
            formData.append('amount', withdrawAmount);
            const res = await axios.post(`${apiUrl}/student/reward/request`, formData);
            if (res.data.status === true || res.data.status === 'success') {
                import('react-hot-toast').then(({ default: toast }) => toast.success(res.data.message || 'Withdrawal request submitted'));
                setWithdrawAmount('');
                fetchRewards(1);
            } else {
                import('react-hot-toast').then(({ default: toast }) => toast.error(res.data.message || 'Failed to submit request'));
            }
        } catch (e) {
            import('react-hot-toast').then(({ default: toast }) => toast.error('Network error. Please try again.'));
        } finally {
            setWithdrawing(false);
        }
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(studentData.referralCode);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const getShareMessage = (code, name) =>
        `🎓 *Join Spyware – Learn & Grow!* 🎓\n\nHey! 👋 I'm *${name}* and I'm learning amazing skills at *Spyware Institute*. You should join too! 🚀\n\n✨ _What you'll get:_\n✅ Expert-led courses\n✅ Hands-on training\n✅ Industry-recognized certificates\n\n🎁 *Use my referral link to register:*\n\n👉 https://www.spywarecomputers.com/courses?referral=${code}\n\n📲 Register now and start your journey today!\n\n_Don't miss out – limited seats available!_ ⏳`;

    const shareViaWhatsApp = () => {
        window.open(`https://wa.me/?text=${encodeURIComponent(getShareMessage(studentData.referralCode, studentData.name))}`, '_blank');
    };

    const shareToClipboard = () => {
        navigator.clipboard.writeText(getShareMessage(studentData.referralCode, studentData.name));
        setShareCopied(true);
        setTimeout(() => setShareCopied(false), 2500);
    };

    const statusStyle = {
        credit: { bg: '#D1FAE5', color: '#059669' },
        debit: { bg: '#FEE2E2', color: '#DC2626' },
        approved: { bg: '#D1FAE5', color: '#059669' },
        request: { bg: '#FEF9C3', color: '#CA8A04' },
        rejected: { bg: '#FEE2E2', color: '#DC2626' },
    };

    return (
        <Box>
            <Container maxWidth="lg">
                <Box sx={{ py: 2 }}>
                    {/* Page Header */}
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="h5" fontWeight={700} mb={1} className="orbitron">
                            Refer & Earn
                        </Typography>
                        <Typography variant="body2" color="text.secondary" fontSize={13}>
                            Invite friends and earn rewards for each successful referral
                        </Typography>
                    </Box>

                    <Grid container spacing={2}>
                        {/* Left Column - Referral Code & Stats */}
                        <Grid item xs={12} md={5}>
                            {/* Referral Code Card */}
                            <Card sx={{
                                borderRadius: 2,
                                backgroundColor: 'white',
                                boxShadow: 'none',
                                border: '1px solid',
                                borderColor: 'rgba(0, 0, 0, 0.07)',
                                mb: 2
                            }}>
                                <CardContent sx={{ p: 2.5 }}>
                                    <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                                        <Box sx={{
                                            width: 32,
                                            height: 32,
                                            borderRadius: 1.5,
                                            backgroundColor: 'rgba(14, 58, 67, 0.1)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            <CardGiftcard sx={{ color: 'primary.main', fontSize: 18 }} />
                                        </Box>
                                        <Typography variant="body2" fontWeight={700} fontSize={14}>
                                            Your Referral Code
                                        </Typography>
                                    </Stack>

                                    <Box sx={{
                                        backgroundColor: 'rgba(14, 58, 67, 0.04)',
                                        borderRadius: 1.5,
                                        p: 1.5,
                                        border: '1.5px dashed',
                                        borderColor: 'primary.main',
                                        mb: 2
                                    }}>
                                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                                            <Typography variant="h6" fontWeight={700} color="primary.main" letterSpacing={2}>
                                                {loading ? <CircularProgress size={18} color="primary" /> : (studentData.referralCode || '—')}
                                            </Typography>
                                            <Tooltip title={copied ? "Copied!" : "Copy"}>
                                                <IconButton
                                                    size="small"
                                                    onClick={copyToClipboard}
                                                    sx={{
                                                        backgroundColor: copied ? '#059669' : 'primary.main',
                                                        color: '#fff',
                                                        width: 32,
                                                        height: 32,
                                                        '&:hover': {
                                                            backgroundColor: copied ? '#047857' : 'primary.dark'
                                                        }
                                                    }}
                                                >
                                                    {copied ? <Check sx={{ fontSize: 16 }} /> : <ContentCopy sx={{ fontSize: 16 }} />}
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    </Box>

                                    <Stack direction="row" spacing={1}>
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            startIcon={<WhatsApp sx={{ fontSize: 18 }} />}
                                            onClick={shareViaWhatsApp}
                                            sx={{
                                                backgroundColor: '#25D366',
                                                '&:hover': { backgroundColor: '#20BA5A' },
                                                textTransform: 'none',
                                                fontWeight: 600,
                                                py: 1,
                                                fontSize: 13
                                            }}
                                        >
                                            WhatsApp
                                        </Button>
                                        <Button
                                            fullWidth
                                            variant="outlined"
                                            startIcon={shareCopied ? <Check sx={{ fontSize: 18 }} /> : <Share sx={{ fontSize: 18 }} />}
                                            onClick={shareToClipboard}
                                            sx={{
                                                borderColor: shareCopied ? '#059669' : 'rgba(0, 0, 0, 0.12)',
                                                color: shareCopied ? '#059669' : 'text.primary',
                                                '&:hover': {
                                                    borderColor: 'primary.main',
                                                    backgroundColor: 'rgba(14, 58, 67, 0.04)'
                                                },
                                                textTransform: 'none',
                                                fontWeight: 600,
                                                py: 1,
                                                fontSize: 13
                                            }}
                                        >
                                            {shareCopied ? 'Copied!' : 'Copy Msg'}
                                        </Button>
                                    </Stack>
                                </CardContent>
                            </Card>

                            {/* Stats Cards */}
                            <Grid container spacing={2} mb={2}>
                                <Grid item xs={6}>
                                    <Card sx={{
                                        borderRadius: 2,
                                        backgroundColor: 'white',
                                        boxShadow: 'none',
                                        border: '1px solid',
                                        borderColor: 'rgba(0, 0, 0, 0.07)'
                                    }}>
                                        <CardContent sx={{ p: 2, textAlign: 'center' }}>
                                            <Box sx={{
                                                width: 40,
                                                height: 40,
                                                borderRadius: 1.5,
                                                backgroundColor: 'rgba(14, 58, 67, 0.1)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                mx: 'auto',
                                                mb: 1
                                            }}>
                                                <People sx={{ color: 'primary.main', fontSize: 22 }} />
                                            </Box>
                                            <Typography variant="h5" fontWeight={700} color="primary.main" mb={0.5}>
                                                {rewardSummary.total_referral}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" fontSize={11} fontWeight={600}>
                                                Referrals
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={6}>
                                    <Card sx={{
                                        borderRadius: 2,
                                        backgroundColor: 'white',
                                        boxShadow: 'none',
                                        border: '1px solid',
                                        borderColor: 'rgba(0, 0, 0, 0.07)'
                                    }}>
                                        <CardContent sx={{ p: 2, textAlign: 'center' }}>
                                            <Box sx={{
                                                width: 40,
                                                height: 40,
                                                borderRadius: 1.5,
                                                backgroundColor: '#FEF3C7',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                mx: 'auto',
                                                mb: 1
                                            }}>
                                                <EmojiEvents sx={{ color: '#D97706', fontSize: 22 }} />
                                            </Box>
                                            <Typography variant="h5" fontWeight={700} color="#D97706" mb={0.5}>
                                                {new Intl.NumberFormat('en-IN').format(rewardSummary.balance ?? 0)}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" fontSize={11} fontWeight={600}>
                                                Coins
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>

                            {/* How It Works */}
                            <Card sx={{
                                borderRadius: 2,
                                backgroundColor: 'white',
                                boxShadow: 'none',
                                border: '1px solid',
                                borderColor: 'rgba(0, 0, 0, 0.07)'
                            }}>
                                <CardContent sx={{ p: 2.5 }}>
                                    <Typography variant="body2" fontWeight={700} mb={2} fontSize={14}>
                                        How It Works
                                    </Typography>

                                    <Stack spacing={1.5}>
                                        <Stack direction="row" spacing={1.5}>
                                            <Box sx={{
                                                width: 24,
                                                height: 24,
                                                borderRadius: '50%',
                                                backgroundColor: 'primary.main',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexShrink: 0
                                            }}>
                                                <Typography variant="caption" color="#fff" fontWeight={700} fontSize={11}>
                                                    1
                                                </Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="body2" fontWeight={600} fontSize={13}>
                                                    Share your code
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" fontSize={11}>
                                                    Send to friends via WhatsApp
                                                </Typography>
                                            </Box>
                                        </Stack>

                                        <Stack direction="row" spacing={1.5}>
                                            <Box sx={{
                                                width: 24,
                                                height: 24,
                                                borderRadius: '50%',
                                                backgroundColor: '#059669',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexShrink: 0
                                            }}>
                                                <Typography variant="caption" color="#fff" fontWeight={700} fontSize={11}>
                                                    2
                                                </Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="body2" fontWeight={600} fontSize={13}>
                                                    Friend enrolls
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" fontSize={11}>
                                                    They join using your code
                                                </Typography>
                                            </Box>
                                        </Stack>

                                        <Stack direction="row" spacing={1.5}>
                                            <Box sx={{
                                                width: 24,
                                                height: 24,
                                                borderRadius: '50%',
                                                backgroundColor: '#D97706',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexShrink: 0
                                            }}>
                                                <Typography variant="caption" color="#fff" fontWeight={700} fontSize={11}>
                                                    3
                                                </Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="body2" fontWeight={600} fontSize={13}>
                                                    You earn rewards
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" fontSize={11}>
                                                    Reward credited to your account
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </Stack>
                                </CardContent>
                            </Card>

                            {/* Withdraw Request */}
                            <Card sx={{ borderRadius: 2, backgroundColor: 'white', boxShadow: 'none', border: '1px solid', borderColor: 'rgba(0, 0, 0, 0.07)', mt: 2 }}>
                                <CardContent sx={{ p: 2.5 }}>
                                    <Typography variant="body2" fontWeight={700} fontSize={14} mb={0.5}>
                                        Withdraw Coins
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" fontSize={11} display="block" mb={1.5}>
                                        Available balance: {new Intl.NumberFormat('en-IN').format(rewardSummary.balance ?? 0)} coins
                                    </Typography>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <TextField
                                            size="small"
                                            type="number"
                                            placeholder="Enter coins"
                                            value={withdrawAmount}
                                            onChange={e => setWithdrawAmount(e.target.value)}
                                            sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: 13 } }}
                                        />
                                        <Button
                                            variant="contained"
                                            disabled={!withdrawAmount || Number(withdrawAmount) <= 0 || withdrawing}
                                            onClick={() => setConfirmWithdraw(true)}
                                            sx={{ borderRadius: 1.5, fontWeight: 600, fontSize: 13, textTransform: 'none', whiteSpace: 'nowrap', py: 1 }}
                                        >
                                            {withdrawing ? <CircularProgress size={18} color="inherit" /> : 'Request'}
                                        </Button>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Right Column - Referral History */}
                        <Grid item xs={12} md={7}>
                            <Card sx={{
                                borderRadius: 2,
                                backgroundColor: 'white',
                                boxShadow: 'none',
                                border: '1px solid',
                                borderColor: 'rgba(0, 0, 0, 0.07)',
                                height: '100%'
                            }}>
                                <CardContent sx={{ p: 2.5 }}>
                                    <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                                        <Box>
                                            <Typography variant="body2" fontWeight={700} fontSize={14}>
                                                Reward History
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" fontSize={11}>
                                                {rewardHistory.length} reward{rewardHistory.length !== 1 ? 's' : ''} found
                                            </Typography>
                                        </Box>
                                    </Stack>

                                    <Divider sx={{ mb: 2 }} />

                                    <Stack spacing={1.5}>
                                        {rewardLoading ? (
                                            <Box sx={{ textAlign: 'center', py: 3 }}>
                                                <CircularProgress size={24} />
                                            </Box>
                                        ) : rewardHistory.length === 0 ? (
                                            <Box sx={{ textAlign: 'center', py: 3 }}>
                                                <Typography variant="body2" color="text.secondary" fontSize={13}>No rewards yet</Typography>
                                            </Box>
                                        ) : rewardHistory.map((row, i) => (
                                            <Box
                                                key={i}
                                                sx={{
                                                    p: 1.5,
                                                    borderRadius: 1.5,
                                                    border: '1px solid',
                                                    borderColor: 'rgba(0, 0, 0, 0.07)',
                                                    transition: 'all 0.2s',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(14, 58, 67, 0.03)',
                                                        borderColor: 'rgba(14, 58, 67, 0.2)'
                                                    }
                                                }}
                                            >
                                                <Stack direction="row" alignItems="center" spacing={1.5}>
                                                    <Box sx={{
                                                        width: 36, height: 36, borderRadius: '50%',
                                                        backgroundColor: 'rgba(14, 58, 67, 0.1)',
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                                                    }}>
                                                        <Person sx={{ color: 'primary.main', fontSize: 20 }} />
                                                    </Box>
                                                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                                        <Typography variant="body2" fontWeight={700} fontSize={13} noWrap>
                                                            {row.referred_student_name || row.reward_from || 'Withdrawal'}
                                                        </Typography>
                                                        <Stack direction="row" alignItems="center" spacing={0.5}>
                                                            <CalendarMonth sx={{ fontSize: 11, color: 'text.secondary' }} />
                                                            <Typography variant="caption" color="text.secondary" fontSize={11}>
                                                                {new Date(row.reward_created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                                                            </Typography>
                                                        </Stack>
                                                    </Box>
                                                    <Stack alignItems="flex-end" spacing={0.5} flexShrink={0}>
                                                        <Typography variant="body2" fontWeight={700}
                                                            color={row.reward_type === 'credit' ? '#059669' : '#DC2626'}
                                                            fontSize={14}
                                                        >
                                                            {row.reward_type === 'credit' ? '+' : '-'}{row.reward_value}
                                                        </Typography>
                                                        {row.reward_status && (
                                                            <Chip
                                                                label={row.reward_status}
                                                                size="small"
                                                                sx={{
                                                                    backgroundColor: (statusStyle[row.reward_status] || statusStyle[row.reward_type])?.bg ?? '#F3F4F6',
                                                                    color: (statusStyle[row.reward_status] || statusStyle[row.reward_type])?.color ?? '#6B7280',
                                                                    fontSize: 10, height: 20, fontWeight: 600,
                                                                    textTransform: 'capitalize'
                                                                }}
                                                            />
                                                        )}
                                                        {row.reward_type && (
                                                            <Chip
                                                                label={row.reward_type}
                                                                size="small"
                                                                sx={{
                                                                    backgroundColor: statusStyle[row.reward_type]?.bg ?? '#F3F4F6',
                                                                    color: statusStyle[row.reward_type]?.color ?? '#6B7280',
                                                                    fontSize: 10, height: 20, fontWeight: 600,
                                                                    textTransform: 'capitalize'
                                                                }}
                                                            />
                                                        )}
                                                    </Stack>
                                                </Stack>
                                            </Box>
                                        ))}
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </Box>
            </Container>
            {/* Confirm Withdraw Dialog */}
            <Dialog open={confirmWithdraw} onClose={() => setConfirmWithdraw(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 700 }}>Confirm Withdrawal</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Submit a withdrawal request for{' '}
                        <strong>
                            {new Intl.NumberFormat('en-IN').format(Number(withdrawAmount) || 0)} coins
                        </strong>?
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setConfirmWithdraw(false)} color="inherit">Cancel</Button>
                    <Button onClick={handleWithdraw} variant="contained" sx={{ fontWeight: 600, textTransform: 'none' }}>
                        Yes, Submit
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>


    );
}
